import type { GameMap } from './map';
import { SeededRng } from './rng';
import type { Entity, EntityComponents, EntityId } from './entity';
import { applyCommand, type Command } from './commands';
import { movementSystem } from './movementSystem';
import { healthSystem } from './health';

export interface WorldOptions {
  map: GameMap;
  /** Seed for the world's PRNG. Defaults to a fixed value for determinism. */
  seed?: number;
}

const DEFAULT_SEED = 1;

/**
 * The components the world keeps a **presence index** for (PRD #825).
 *
 * Both are sparse and structural: a projectile or a teleporter is created with
 * its component and never gains one afterwards, and most worlds hold none of
 * either. Their per-tick systems ({@link stepProjectiles}, {@link
 * teleportersByCell}) used to open with a full `world.all()` scan, so an Arena
 * run world paid an O(entities) walk every tick for two subsystems it has not
 * used since ADR-0216 - at benchmark density that is ~375 entities x 2 walks x
 * 20 Hz x 12 concurrent runs of pure waste.
 *
 * The index is maintained in {@link World.createEntity}/{@link World.add}/{@link
 * World.remove}, which is O(1) per mutation and needs no invalidation. **The
 * invariant it rests on: a component in this list is attached at entity
 * creation, never assigned onto a live entity.** Adding a key here without that
 * property would silently under-report; `world.test.ts` pins both producers.
 */
export const INDEXED_COMPONENTS = ['teleporter', 'projectile'] as const;

/** A component {@link World} indexes by presence; see {@link INDEXED_COMPONENTS}. */
export type IndexedComponent = (typeof INDEXED_COMPONENTS)[number];

/**
 * The single aggregate that owns all game state: the {@link GameMap}, every
 * {@link Entity} keyed by {@link EntityId}, and a seeded {@link SeededRng}.
 *
 * It is the sole mutator of game state and the unit that lives on the
 * authoritative server. It owns entity CRUD + id allocation, a seeded RNG, a
 * command queue, and the fixed-timestep {@link tick}: input enqueues commands,
 * and each step drains them in FIFO order through the pure {@link applyCommand}
 * reducer before running the systems. It imports no rendering and no Pixi,
 * staying on the sim side of the hard architectural split, and reads no wall
 * clock - all time enters through {@link tick}.
 */
export class World {
  readonly map: GameMap;
  readonly rng: SeededRng;
  private readonly entities = new Map<EntityId, Entity>();
  /**
   * Memoized snapshot of {@link entities} in insertion order, returned by
   * {@link all}. The render loop calls `all()` several times per frame and the
   * read-model world is immutable between snapshots (a fresh {@link World} is
   * rebuilt from each server snapshot), so caching the array turns those calls
   * from an O(n) spread each into a single spread per structural change. Set to
   * `undefined` whenever the entity set changes ({@link createEntity},
   * {@link add}, {@link remove}), so the next `all()` rebuilds it. Callers must
   * treat the result as read-only (its type enforces this) - it is a shared
   * reference, not a fresh copy.
   *
   * A true `#private` field (not a `private`-keyword one) so it stays out of
   * structural equality: it is a derived cache, not part of the world's logical
   * state, and the persistence round-trip compares whole worlds with `toEqual`.
   */
  #cachedAll: Entity[] | undefined;
  /**
   * The {@link INDEXED_COMPONENTS} presence index: one live set per indexed
   * component, maintained by the entity CRUD below. A true `#private` field for
   * the same reason as {@link #cachedAll} - it is derived state, not part of the
   * world's logical identity, and the persistence round-trip compares whole
   * worlds with `toEqual`.
   */
  readonly #indexed: { readonly [K in IndexedComponent]: Set<Entity> } = {
    teleporter: new Set(),
    projectile: new Set(),
  };
  /** Pending commands, drained in FIFO order at the start of each fixed step. */
  private commandQueue: Command[] = [];
  /** Monotonic id counter; never decremented, so ids stay unique per world. */
  private nextEntityId = 1;

  constructor(opts: WorldOptions) {
    this.map = opts.map;
    this.rng = new SeededRng(opts.seed ?? DEFAULT_SEED);
  }

  /** Allocate the next stable, unique entity id. */
  allocateId(): EntityId {
    return this.nextEntityId++ as EntityId;
  }

  /**
   * The id the next {@link allocateId} call will return; exposed so persistence
   * can capture and restore the allocator, keeping ids stable and collision-free
   * across a serialize/deserialize round-trip.
   */
  get idCounter(): number {
    return this.nextEntityId;
  }

  set idCounter(value: number) {
    this.nextEntityId = value;
  }

  /**
   * The pending (undrained) commands, in FIFO order, for serialization. Returns
   * the live queue as a read-only view; restore it by re-{@link enqueue}ing each
   * command on a fresh world.
   */
  get pendingCommands(): readonly Command[] {
    return this.commandQueue;
  }

  /** Create an entity with a freshly allocated id and register it. */
  createEntity(components: EntityComponents = {}): Entity {
    const entity: Entity = { id: this.allocateId(), ...components };
    this.entities.set(entity.id, entity);
    this.#cachedAll = undefined;
    this.#index(entity);
    return entity;
  }

  /** Register a pre-built entity (e.g. one rehydrated from serialization). */
  add<E extends Entity>(entity: E): E {
    // An id already in use is replaced, so the displaced object must leave the
    // presence index with it or it would linger there forever.
    const displaced = this.entities.get(entity.id);
    if (displaced !== undefined && displaced !== entity) this.#unindex(displaced);
    this.entities.set(entity.id, entity);
    this.#cachedAll = undefined;
    this.#index(entity);
    return entity;
  }

  /** File a freshly registered entity under each indexed component it carries. */
  #index(entity: Entity): void {
    for (const key of INDEXED_COMPONENTS) {
      if (entity[key] !== undefined) this.#indexed[key].add(entity);
    }
  }

  /** Drop a departing entity from every index it was filed under. */
  #unindex(entity: Entity): void {
    for (const key of INDEXED_COMPONENTS) {
      if (entity[key] !== undefined) this.#indexed[key].delete(entity);
    }
  }

  /**
   * Every entity carrying `key` (see {@link INDEXED_COMPONENTS}). Returns the
   * live index set, so a caller must treat it as read-only and must not mutate
   * the world while iterating it. The common answer is the empty set, which is
   * the point: a per-tick system can bail in O(1) instead of scanning the world.
   */
  withComponent(key: IndexedComponent): ReadonlySet<Entity> {
    return this.#indexed[key];
  }

  /** Look up an entity by id, or `undefined` if absent. */
  get(id: EntityId): Entity | undefined {
    return this.entities.get(id);
  }

  /** Remove an entity by id; returns whether it was present. */
  remove(id: EntityId): boolean {
    const entity = this.entities.get(id);
    if (entity === undefined) return false;
    this.entities.delete(id);
    this.#cachedAll = undefined;
    this.#unindex(entity);
    return true;
  }

  /**
   * Every entity currently in the world (insertion order). Returns a cached,
   * **read-only** array that stays valid until the entity set changes, so the
   * render loop's several calls per frame share one spread. Do not mutate it -
   * copy first (`[...world.all()]`) if you need a mutable list.
   */
  all(): readonly Entity[] {
    return (this.#cachedAll ??= [...this.entities.values()]);
  }

  /** Number of entities in the world. */
  get count(): number {
    return this.entities.size;
  }

  /**
   * Enqueue a command (intent). It is not applied immediately; it waits in FIFO
   * order until the next {@link tick} drains the queue. This is the single seam
   * the authoritative server owns - input only ever enqueues.
   */
  enqueue(command: Command): void {
    this.commandQueue.push(command);
  }

  /**
   * Replace the pending queue wholesale. A **state setter, not a gameplay seam**
   * (the `idCounter` setter's twin): the client replica adopts each wire tick's
   * authoritative `commandQueue` in place (ADR-0324) instead of rebuilding the
   * whole world just to re-enqueue it. Takes ownership of the array (callers
   * pass a freshly decoded/copied one, exactly as {@link enqueue} receives
   * caller-owned commands).
   */
  replacePendingCommands(commands: Command[]): void {
    this.commandQueue = commands;
  }

  /**
   * Advance the world by one fixed timestep of `stepMs` milliseconds. The tick
   * order is:
   *
   *   1. drain the command queue (player input) in FIFO order through the pure
   *      {@link applyCommand} reducer.
   *   2. {@link movementSystem} - advance every mobile's step/turn state machine.
   *   3. {@link healthSystem} - passively regenerate every mobile's health toward
   *      its maximum (Duel slice A, ADR-0128), unless its regen is paused.
   *
   * The same commands and steps always produce the same world state - the sim
   * never reads the wall clock or unseeded randomness.
   */
  tick(stepMs: number): void {
    if (this.commandQueue.length > 0) {
      const commands = this.commandQueue;
      this.commandQueue = [];
      for (const command of commands) applyCommand(this, command);
    }
    movementSystem(this, stepMs);
    healthSystem(this, stepMs);
  }
}
